import Autolinker from 'autolinker';
import {countries} from '../../config/countries';
import {addZero, getDayEnd, getDayStart, getNextHour, stringToTime} from '../tools/Tools';
import {linkifyContent, stripQuoteLink, stripRepostPrefix} from './socialContent';
import {dayPoints, dayWindow} from './timelineDays';
import {SocialNetwork} from './SocialNetwork';

interface CallbackOneParam<T1, T2 = void> {
  (param1: T1): T2;
}

export type ApiParamsType = {
    [key: string]: string | number ;
}

export type ApiSuccessResponse = {
    code : number
    data: []
}


export type ContributorType = {
    type:string;
    name:string;
}

export const allowedContributorTypes = ['eppress', 'mep', 'president', 'committee', 'grouppress', 'otherinstitution'];


/**
 * The channels page's tabs, in order. `type` is the contributor type the API filters on; every one
 * but "all" needs a slug in channelsPath, or its tab silently opens All (a test holds that).
 */
export const contributorTypeTabs: ReadonlyArray<{type:string, original:string, name:string}> = [
    { type: 'all', original: 'All', name: 'All' },
    { type: 'president', original: 'President', name: 'President' },
    { type: 'mep', original: 'mep', name: 'MEPs' },
    { type: 'grouppress', original: 'grouppress', name: 'Political Groups' },
    { type: 'committee', original: 'committee', name: 'Committees' },
    { type: 'eppress', original: 'eppress', name: 'EP Accounts' },
    { type: 'otherinstitution', original: 'otherinstitution', name: 'Other Institutions' },
];


const virality: string[] = [
    "low",
    "medium",
    "high",
    "trending",
];

/**
 * Display order of the political groups, largest first, then the unattached last. Tenth term
 * (2024-2029). Codes absent from this list sort to the front via indexOf's -1, so a group the API
 * adds later is visible rather than silently last - but it should be added here deliberately.
 */
const ePartyOrder = [
    'EPP',
    'S&D',
    'PfE',
    'ECR',
    'Renew',
    'Greens/EFA',
    'The Left',
    'ESN',
    'NI',
];



var politicalGroupsIds:any = [];

/**
 * The API is served over https everywhere it is deployed, and requests are upgraded so a stray
 * http:// in configuration cannot downgrade them. A local API has no certificate, though, so
 * upgrading a loopback address just makes the app unusable against a dev backend.
 */
function forceHttps(url:string):string{
    if(/^https?:\/\/(localhost|127\.0\.0\.1|\[::1\])(:|\/|$)/.test(url)) return url;
    return url.replace('http:','https:');
}

/**
 * The group's short code off an API payload. The field is `eParty`; the client has always called it
 * `eparty`, and reading the client's spelling off the API's object is how both the lookup table and
 * every contributor ended up with undefined here - which is why this is written once.
 */
function epartyCode(politicalGroup:any):string|undefined {
    if(!politicalGroup) return undefined;
    return politicalGroup.eParty ?? politicalGroup.eparty;
}

function getEpartyId(eparty:string|undefined){
    if(!eparty) return 0;
    let party:any[] = politicalGroupsIds.filter((p:any,i:number) =>  p[0] === eparty);
    if(party.length < 1) return 0;
    return party[0][1];
}

function timeToHoursMinutes(time:number):string{
    let num = time/60;
    let hours = (num / 60);
    let rhours = Math.floor(hours);
    let minutes = (hours - rhours) * 60;
    let rminutes = Math.round(minutes);
    let text = '';
    if(rhours > 0 ) text +=  rhours + "h ";
    if(rminutes > 0 ) text +=  rminutes + "m ";
    if (text.length === 0) text = '1m ';
    return text + 'ago';
}

function getBrowserLanguage(){
    // @ts-ignore
    let lang =  navigator.language || navigator.userLanguage || 'en';
    if(lang.indexOf('-') !== -1 ) lang = (lang.split('-')[0]).toLowerCase();
    return lang;
}

function timeToDate(time:number):string{
    let currentDate = new Date(time*1000);
    let cDay = currentDate.getUTCDate();
    let cMonth = currentDate.getUTCMonth() + 1;
    let cYear = currentDate.getUTCFullYear();
    return `${cDay}/${cMonth}/${cYear}`;
}


export function timeAgo(time:number):string{
    const h24 = 3600 * 24 ;
    let ago:number = (Date.now()/1000)-time;
    if(ago < h24) return timeToHoursMinutes(ago);
    return timeToDate(time);
}


function getCountryName(key:string){
    for(let item of countries){
        if(item.key === key) return item.name;
    }
    return '';
}

export function parseContributor(item:any){
    if(!item) return null;
    let politicalGroup:any = {
        name: '',
        party: '',
        eparty :'',
        picture: ''
    };

    let socialNetworks:any =  {};

    if(item.politicalGroup) {


        const eparty = epartyCode(item.politicalGroup);

        politicalGroup = {
            name: '',
            eparty:  eparty,
            party: item.politicalGroup.party,
            localParty:item.politicalGroup.localParty,
            picture: item.eParty,
            id: getEpartyId(eparty)
        };
    }

    if(item.socialNetworks){
        for(let i in item.socialNetworks){
            let type = item.socialNetworks[i].type;
            socialNetworks[type] = {...item.socialNetworks[i]};
        }
    }

    return  {
        id: item.id,
        avatar: item.pictureLink,
        country:  getCountryName(item.country),
        // An institution has no person name, only fullName ("Council of the EU"); without this it
        // showed nameless on every card, channel list and contributor page.
        firstName: item.firstName || item.lastName ? (item.firstName || '') : (item.fullName || ''),
        lastName: item.lastName || '',
        politicalGroup:politicalGroup,
        twitter: item.sourceUrl,
        socialNetworks:socialNetworks,
        // The API sends contributor.type as a string ("president", "eppress", "mep"). Reading
        // item.type.type off a string is undefined, so this was null for every contributor - which
        // also meant SocialCard's `mep.type === "mep"` avatar check could never be true. The object
        // form is kept as a fallback in case some other endpoint still sends one.
        type: (typeof item.type === "string" ? item.type : item.type?.type) || null

    }
}


function parseImage(img:string):string{
    if(img.indexOf('http') !== -1) return img;
    return process.env.REACT_APP_APIURL +'/images/'+ encodeURIComponent (img) ;
}

/** The contributor as parsed, with `fallback` as its name when it has none of its own. */
function named(contributor:any, fallback?:string){
    if(!contributor || contributor.firstName || contributor.lastName || !fallback) return contributor;
    return {...contributor, firstName: fallback};
}

export function parseSocialItem(item:any){
    // The API says which network a post came from; assuming twitter linkified bluesky mentions
    // into twitter.com URLs. The fallback keeps items from an API that predates the field working.
    const source: SocialNetwork = item.source?.type ?? 'twitter';
    var mediaUrl = null;
    if(item.media){
        // media.url is our own copy of the image the card shows, once the API's media import has
        // made it (ops issues/074); the proxy tokens (src for a photo, previewImageUrl for a video)
        // are what every post has from the start, and what posts stored before the import keep.
        const copy = item.media.url ? parseImage(item.media.url) : null;
        switch(item.media.type){
            case 'photo':
                if(copy || item.media.src){
                    mediaUrl ={
                        src:  copy ?? parseImage(item.media.src),
                        type: 'photo'
                    };
                }
                break;
            case 'video':
                if(copy || item.media.previewImageUrl){
                    mediaUrl ={
                        src:  copy ?? parseImage(item.media.previewImageUrl),
                        type: 'video'
                    };
                }
                break;
            case 'animated_gif':
                // X sends a still for an animation and no image URL; the still is the picture.
                if(copy || item.media.previewImageUrl){
                    mediaUrl ={
                        src:  copy ?? parseImage(item.media.previewImageUrl),
                        type: 'photo'
                    };
                }
                break;
        }
    }

    return {
        id: item.id,
        content: linkifyContent(
            stripQuoteLink(stripRepostPrefix(item.content, item.repostedFrom, item.postType),
                item.postType, item.quoted?.url),
            source),
        media:mediaUrl,
        // A post's contributor carries no fullName; for an account with no person name, the name
        // it goes by on its network is the best there is.
        mep: named(parseContributor(item.contributor), item.source?.name),
        sourceUrl: item.shareUrl,
        source: source,
        // The account's display name, which is the only name an institutional contributor has: the
        // embedded contributor object carries empty firstName/lastName for those.
        accountName: item.source?.name ?? undefined,
        postType: item.postType ?? 'original',
        repostedFrom: item.repostedFrom ?? undefined,
        inReplyTo: item.inReplyTo ?? undefined,
        quoted: item.quoted && item.quoted.url ? {
            handle: item.quoted.handle,
            displayName: item.quoted.displayName ?? undefined,
            avatarUrl: item.quoted.avatarUrl ? parseImage(item.quoted.avatarUrl) : undefined,
            text: item.quoted.text ?? undefined,
            url: item.quoted.url,
        } : undefined,
        postedAt: new Date(item.postedAt * 1000),
        timeAgo: timeAgo(item.postedAt),
    }
}





class ApiProvider {
    private _baseUrl: string;
    private _requestUiids:ApiParamsType = {};

    constructor(config:any){
        this._baseUrl = config.baseUrl || '';
    }

    private createError(code:number = 500, message:string = 'unkown_error'){
        return {code:code, message:message};
    }

    private paramsToQuery(params:ApiParamsType):string{
        return Object.keys(params).map(function(prop) {
            return [prop, encodeURIComponent(  decodeURIComponent(''+params[prop]) )].join("=");
          }).join("&");
    }

    private sanitizeUrl(url:string){
        let parts = url.split('?');
        if(typeof parts[1] !== 'undefined'){
            let params = new URLSearchParams(parts[1]);
            let params_array:ApiParamsType = {};
            //@ts-ignore
            for (let value of params.keys()) {
              params_array[value] = ''+params.get(value);
            }
            parts[1] = this.paramsToQuery(params_array);
        }
        url = parts.join('?');
        console.log('sanitized url',url );
        return forceHttps(url);

    }

    private postFeedback(url:string,params:any, resolve:CallbackOneParam<any>, reject:CallbackOneParam<any>)
    {
        const options = {
            method: 'POST',
            body: JSON.stringify(params),
            headers: {
                'Content-Type': 'application/json'
            }
        };
        url = forceHttps(url);
        fetch(url, options)
        .then((respose) => respose.json())
        .then((json)=> {

            if(json.type !== 'success') reject(this.createError(json.code, json.data));
            resolve(json.data);
        })
        .catch((error) => {
            reject(this.createError(500 , error.message));
        });
    }

    private  getRequest(url:string,params:ApiParamsType|null, resolve:CallbackOneParam<any>, reject:CallbackOneParam<any>, uuid:string = ''){
        let paramsString = (params!== null)? '?'+ this.paramsToQuery(params): '';
        url = this.sanitizeUrl(url) +  paramsString;
        console.log('API REQUEST: ' + url );

        Promise.all([fetch(url).then((respose) => respose.json()), this.loadGroupIds()])
            .then(([json])=> {

                if(json.type !== 'success') reject(this.createError(json.code, json.data));
                resolve({data:json.data,uuid:uuid});
            })
            .catch((error) => {
                reject({error:this.createError(500 , error.message),uuid:error});
            });
    }

    /**
     * Political group to the id of its group-press contributor, which the group pill on a card links
     * to. Loaded once, on the first request, and every response waits for it, so no contributor is
     * parsed before it is there. It used to be a synchronous request made when this module was
     * imported: it held up the page's start, and sent every test suite that imports this module to
     * the API (ops issues/063). If it fails, the pills have no link.
     */
    private groupIdsLoaded: Promise<void> | null = null;

    private loadGroupIds(): Promise<void> {
        if(this.groupIdsLoaded === null){
            this.groupIdsLoaded = fetch(this.sanitizeUrl(this._baseUrl + '/contributor/?pageNo=0&type=grouppress'))
                .then((response) => response.json())
                .then((json) => {
                    politicalGroupsIds = (json?.data?.items || [])
                        .map((item:any) => [epartyCode(item.politicalGroup), item.id ])
                        .filter((entry:any[]) => entry[0]);
                })
                .catch(() => {});
        }
        return this.groupIdsLoaded;
    }

    //params can be a url
    public getSocialItems(params:ApiParamsType|string, providerId?:number){
        let queryParams:ApiParamsType|null;
        let url:string;
        if(typeof(params) === 'string'){
            url = params;
            queryParams =  null;
        }else {
            url = this._baseUrl+'/social-item/';
            //if(typeof providerId !== 'undefined') url += `${providerId}/`;
            queryParams = params;
            if(typeof providerId !== 'undefined'){
                queryParams['contributors_ids'] = providerId;
            }

        }

        let uuid = Date.now()+'_'+Math.floor(Math.random() * 100);
        this._requestUiids['socialiTems'] = uuid;

        return new Promise<any> ((resolve, reject) => {
            this.getRequest(url,queryParams,
                (response) => {
                    let uuid = response.uuid;
                    if(uuid !== this._requestUiids['socialiTems']) return;

                    let data = response.data;
                    let items:any = [];
                    for(let i in data.items ) {
                        let item:any= parseSocialItem(data.items[i]);
                        items.push(item);
                    }

                    resolve({items:items, next:data.next,total:data.total});
                },
                (response) => {
                    let uuid = response.uuid;
                    if(uuid !== this._requestUiids['socialiTems']) return;

                    let error = response.error;
                    reject(error);
                },
                uuid
            );
        });
    }


    getTopics (params:ApiParamsType,providerId?:number){

        const url = (typeof providerId !== 'undefined' )?  this._baseUrl+`/topic/${providerId}/` : this._baseUrl+'/topic/';

        if(typeof providerId === 'undefined' ){
            params['contributorType'] = allowedContributorTypes.join(',');
        }

        return new Promise<any> ((resolve, reject) => {
            this.getRequest(url,params,
                (response) => {
                    let data = response.data;
                    let topics:any = []
                    let maxValue = 0;
                    for(let i in data){
                        let value =  parseInt(data[i].virality);
                        if(value > maxValue) maxValue = value;
                        topics.push({
                            id:data[i].content,
                            content: data[i].content,
                            value: value -1, //fix value 4 in index operation .
                            virality:''
                        });

                    }
                    for(let i in topics){
                        let viralityIndex = Math.floor(topics[i].value *100 / (maxValue * 25));
                        topics[i].virality = virality[viralityIndex];
                    }

                    resolve(topics);
                },

                (response) => {
                    let error = response.error;
                    reject(error);
                }
            );
        });
    }

    getPoliticalGroups(){
        const url = this._baseUrl+'/type/eparty/';
        return new Promise<any> ((resolve, reject) => {
            this.getRequest(url,null,
                (response) => {
                    let data = response.data;
                    let filtered = data.filter((party:any, index:number, arr:any) => {
                        return party.fullName != null;
                    });
                    // The API sends `eParty`; every consumer here reads `eparty`. Normalising once,
                    // at the edge, is what the rest of this client already assumes has happened -
                    // reading the client's spelling straight off the API object is why every pill
                    // rendered an empty label. Same helper the contributor parser uses.
                    let normalised = filtered.map((party:any) => ({
                        ...party,
                        eparty: epartyCode(party),
                        fullname: party.fullName,
                    }));
                    let ordered = normalised.sort((x:any,y:any) => {
                        let xIndex = ePartyOrder.indexOf(x.eparty);
                        let yIndex = ePartyOrder.indexOf(y.eparty);
                        if( xIndex > yIndex ) return 1
                        if (xIndex <  yIndex) return -1
                        return 0;
                    });
                  resolve(ordered);
                },

                (response) => {
                    let error = response.error;
                    reject(error);
                }
            );
        });
    }

    getContributorTypes(){
        const url = this._baseUrl+'/type/contributor/';
        return new Promise<any> ((resolve, reject) => {
            const contributors:any = contributorTypeTabs;

            resolve(contributors);
        });
    }

    getContributor(id:string){

        const url = this._baseUrl+'/contributor/'+ id +'/';
        return new Promise<any> ((resolve, reject) => {
            this.getRequest(url,null,
                (response) => {
                    let data = response.data;
                    resolve(parseContributor(data));
                },

                (response) => {
                    let error = response.error;
                    reject(error);
                }
            );
        });

    }

    getContributors(params:ApiParamsType|string){
        let url:string;
        let queryParams:ApiParamsType|null;
        if(typeof(params) === 'string'){
            url = params;
            queryParams =  null;
        }else {
            url = this._baseUrl+'/contributor/';
            queryParams = params;
        }

        return new Promise<any> ((resolve, reject) => {
            this.getRequest(url,queryParams,
                (response) => {
                    let data  = response.data;
                    let total = data.total;
                    let items:any = [];
                    if(data.items !== null) {
                        const filteredItems = data.items;
                        for(let i in filteredItems){
                            items.push(parseContributor(filteredItems[i]));
                        }
                    }

                //@ts-ignore
                if (queryParams && typeof queryParams['type'] !== 'undefined' && queryParams['type']=== 'grouppress'){
                    items = items.sort((x:any,y:any) => {
                        let xIndex = ePartyOrder.indexOf(x.politicalGroup.eparty);
                        let yIndex = ePartyOrder.indexOf(y.politicalGroup.eparty);
                        if( xIndex > yIndex ) return 1
                        if (xIndex <  yIndex) return -1
                        return 0;
                    });

                }
                resolve({contributors: items, next:data.next, total:total});
                },

                (response) => {
                    let error = response.error;
                    console.log('ERROR',error);
                    reject(error);
                }
            );
        });
    }

    getTranslation(itemId:string){
        const url = this._baseUrl+'/translate/'+itemId+'/'+ getBrowserLanguage();
        return new Promise<any> ((resolve, reject) => {
            this.getRequest(url,null,
                (response) => {
                    let data = response.data;
                    resolve(data);
                },
                (response) => {
                    let error = response.error;
                    reject(error);
                }
            );
        });
    }

    getSuggestion(text:string){
        // The text goes in the path, so it has to be encoded: a '#' otherwise starts the URL
        // fragment and the API receives nothing, and a '/' invents a path segment.
        const url = this._baseUrl+'/tip/'+encodeURIComponent(text)+'/';
        return new Promise<any> ((resolve, reject) => {
            this.getRequest(url,null,
                (response) => {
                    let data = response.data;
                    resolve(data);
                },
                (response) => {
                    let error = response.error;
                    reject(error);
                }
            );
        });
    }

    getTimeline(key:string){
            switch(key){
            case 'day':
                return this.getTimeLine24h();

            case 'week':
                return this.getTimeLineDays(7);

            case 'month':
                return this.getTimeLineDays(30);

            default:
                return new Promise<any> ((resolve, reject) => {
                    resolve([]);
                });
        }

    }

    getTimeLineDays(days:number){
        const url = this._baseUrl+'/time-line/';
        const now = Date.now();
        const {from, to} = dayWindow(days, now);

        return new Promise<any> ((resolve, reject) => {
            this.getRequest(url,{from:from, to:to},
                (response) => {
                    resolve(dayPoints(response.data, days, now));
                },
                (response) => {
                    let error = response.error;
                    reject(error);
                }
            );
        });

    }

    getTimeLine24h() {
        let url = this._baseUrl+'/time-line/l24h';
        let expected:number[] = [];
        let now = getNextHour();
        //all hours expected
        for(let i = 0; i <= 23; i++ ){
            let current =  new Date(now - (i * 3600000));
            let hour:string = addZero(current.getUTCHours());
            let day:string = addZero(current.getUTCDate());
            let month:string = addZero(current.getUTCMonth()+1);
            let year:string = addZero(current.getUTCFullYear());
             expected.unshift(parseInt( year+month+day+hour) );
        }

        return new Promise<any> ((resolve, reject) => {
            this.getRequest(url,null,
            (response) => {
                let data = response.data;
                // complete hous without data
                let dataCompleted:any = expected.map((date:number,i:number) => {
                    let r = data.filter( (item:any,i:number) => {
                        return item.day === date;
                    });
                    return (r.length > 0 )? r[0] : {day:date, total:0};
                });

                let timeline:any[] = [];
                let total = 0;
                let from:number = 0;

                timeline = dataCompleted.map( (item:any,i:number) => {
                    let time = stringToTime(item.day);
                    if(i === 0) from = getDayStart(Math.floor(time/1000));

                    let date = new Date(time);
                    let x = addZero(date.getHours())+':00'; //local Hour
                    // The last point is the next hour, the end of the window: left out of the line
                    // rather than drawn at 0 (issues/014).
                    let y = (i === dataCompleted.length - 1) ? null : item.total;
                    total += item.total;
                    return {x:x,y:y,time:time};

                });
                resolve({timeline:timeline, from:from, to:getDayEnd(),total:total});

            },
            (response) => {
                let error = response.error;
                reject(error);
            });
        });
    }

    sendFeedBack(params:any){
        let url = this._baseUrl+'/feedback/send/';
        return new Promise<any> ((resolve, reject) => {
            this.postFeedback(url,params,
                (data) => {
                    resolve(data);
                },
                (error) => {
                    reject(error);
                }
            );
        });

    }

}


const apiProvider = new ApiProvider({baseUrl:process.env.REACT_APP_APIURL});
export {apiProvider};

